import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlus, FiTrash2, FiDownload, FiUpload, FiCheck, FiX, FiFileText } from 'react-icons/fi';
import api from '../../services/api';

const STATUS_OPTIONS = ['Draft', 'Open', 'Paid', 'Void'];

const AccountingTab = ({ shipmentId, shipmentType = 'truck' }) => {
  const navigate = useNavigate();
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [memo, setMemo] = useState('');
  const [includeDraft, setIncludeDraft] = useState(true);
  const [showLoadTP, setShowLoadTP] = useState(false);
  const [tpSearch, setTpSearch] = useState('');
  const [tpResults, setTpResults] = useState([]);
  const [loadingTP, setLoadingTP] = useState(false);

  // Invoice upload state
  const invoiceInputRef = useRef(null);
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [invoiceParsing, setInvoiceParsing] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceCreating, setInvoiceCreating] = useState(false);

  const handleInvoiceUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF invoice file');
      return;
    }
    setInvoiceParsing(true);
    setInvoiceModal(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/import/parse-invoice', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setInvoiceData(res.data.data);
      } else {
        toast.error(res.data.error || 'Failed to parse invoice');
        setInvoiceModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to parse invoice');
      setInvoiceModal(false);
    } finally {
      setInvoiceParsing(false);
      if (invoiceInputRef.current) invoiceInputRef.current.value = '';
    }
  };

  const handleInvoiceFieldChange = (field, value) => {
    setInvoiceData(prev => ({ ...prev, [field]: value }));
  };

  const handleChargeAmountChange = (index, value) => {
    setInvoiceData(prev => {
      const charges = [...prev.charges];
      charges[index] = { ...charges[index], amount: parseFloat(value) || 0 };
      return { ...prev, charges, total_amount: charges.reduce((s, c) => s + (c.amount || 0), 0) };
    });
  };

  const handleChargeDescChange = (index, value) => {
    setInvoiceData(prev => {
      const charges = [...prev.charges];
      charges[index] = { ...charges[index], description: value };
      return { ...prev, charges };
    });
  };

  const removeChargeLine = (index) => {
    setInvoiceData(prev => {
      const charges = prev.charges.filter((_, i) => i !== index);
      return { ...prev, charges, total_amount: charges.reduce((s, c) => s + (c.amount || 0), 0) };
    });
  };

  const addChargeLine = () => {
    setInvoiceData(prev => ({
      ...prev,
      charges: [...prev.charges, { description: '', amount: 0 }],
    }));
  };

  const createAPFromInvoice = async () => {
    if (!invoiceData || invoiceData.charges.length === 0) {
      toast.error('No charges to import');
      return;
    }
    setInvoiceCreating(true);
    try {
      // Create AP charge header
      const chargeRes = await api.post(`/charges/shipment/${shipmentId}`, {
        charge_type: 'AP',
        shipment_type: shipmentType,
        invoice_no: invoiceData.invoice_no || '',
        party: invoiceData.vendor || '',
        vendor: invoiceData.vendor || '',
        vendor_invoice_no: invoiceData.invoice_no || '',
        invoice_date: invoiceData.invoice_date || '',
        due_date: invoiceData.due_date || '',
        cost: invoiceData.total_amount || 0,
        status: 'Open',
        invoice_received: true,
      });

      if (chargeRes.data?.id) {
        // Create each charge line item
        for (let i = 0; i < invoiceData.charges.length; i++) {
          const ch = invoiceData.charges[i];
          if (ch.amount > 0 || ch.description) {
            await api.post(`/charge-items/charge/${chargeRes.data.id}`, {
              freight_description: ch.description || 'Charge',
              pc: 'C',
              unit: 'SHIPMENT',
              volume: 1,
              rate: ch.amount || 0,
              amount: ch.amount || 0,
              status: 'Open',
              sort_order: i + 1,
            });
          }
        }

        toast.success(`AP created: ${invoiceData.charges.length} charge(s), total $${invoiceData.total_amount.toFixed(2)}`);
        setInvoiceModal(false);
        setInvoiceData(null);
        loadData(); // Refresh charges
      }
    } catch (error) {
      console.error('Create AP error:', error);
      toast.error('Failed to create AP charge');
    } finally {
      setInvoiceCreating(false);
    }
  };

  const loadData = useCallback(async () => {
    if (!shipmentId) return;
    try {
      // shipment_type or the tab shows another module's shipment with the
      // same id — every module numbers its shipments from 1.
      const res = await api.get(`/charges/shipment/${shipmentId}`,
        { params: { shipment_type: shipmentType } });
      setCharges(res.data);
    } catch (error) {
      console.error('Failed to load accounting data:', error);
    } finally {
      setLoading(false);
    }
  }, [shipmentId, shipmentType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Group charges by type
  const arCharges = charges.filter(c => c.charge_type === 'AR');
  const dcCharges = charges.filter(c => c.charge_type === 'DC');
  const apCharges = charges.filter(c => c.charge_type === 'AP');

  // Filter by draft
  const filterCharge = (c) => includeDraft || c.status !== 'Draft';
  const filteredAR = arCharges.filter(filterCharge);
  const filteredDC = dcCharges.filter(filterCharge);
  const filteredAP = apCharges.filter(filterCharge);
  const allFiltered = [...filteredAR, ...filteredDC, ...filteredAP];

  // Totals
  const totalRevenue = allFiltered.reduce((sum, c) => sum + (Number(c.revenue) || 0), 0);
  const totalCost = allFiltered.reduce((sum, c) => sum + (Number(c.cost) || 0), 0);
  const totalBalance = totalRevenue - totalCost;
  const totalProfit = totalRevenue - totalCost;
  const profitPercentage = totalCost !== 0 ? ((totalProfit / totalCost) * 100) : 0;
  const profitMargin = totalRevenue !== 0 ? ((totalProfit / totalRevenue) * 100) : 0;

  // Navigate to new charge detail page
  const goToNewCharge = (chargeType) => {
    navigate(`/charge/new?shipmentId=${shipmentId}&type=${chargeType}&shipmentType=${shipmentType}`);
  };

  const updateCharge = async (id, field, value) => {
    setCharges(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
    try {
      await api.put(`/charges/${id}`, { [field]: value });
    } catch (error) {
      toast.error('Failed to update');
      loadData();
    }
  };

  const deleteCharge = async (id) => {
    try {
      await api.delete(`/charges/${id}`);
      setCharges(prev => prev.filter(c => c.id !== id));
      toast.success('Deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Trade partner search for default freight
  const searchTP = async (query) => {
    setTpSearch(query);
    if (query.length < 1) { setTpResults([]); return; }
    try {
      const res = await api.get(`/trade-partners/search?q=${encodeURIComponent(query)}`);
      setTpResults(res.data || []);
    } catch { setTpResults([]); }
  };

  const loadDefaultFreight = async (tp) => {
    try {
      setLoadingTP(true);
      const res = await api.get(`/trade-partners/${tp.id}/default-charges`);
      const defaults = res.data || [];
      if (defaults.length === 0) {
        toast.info(`No default charges found for ${tp.name}`);
        setShowLoadTP(false);
        setLoadingTP(false);
        return;
      }

      // Create charges from defaults
      let created = 0;
      for (const dc of defaults) {
        const chargeType = dc.charge_section === 'AR' ? 'AR' : dc.charge_section === 'AP' ? 'AP' : 'DC';
        try {
          // Create the charge header
          const chargeRes = await api.post('/charges', {
            shipment_id: shipmentId,
            charge_type: chargeType,
            shipment_type: shipmentType,
            party: tp.name,
            description: `Default from ${tp.code}`,
            revenue: chargeType === 'AR' ? (dc.amount || 0) : 0,
            cost: chargeType === 'AP' ? (dc.amount || 0) : 0,
            status: 'Draft',
          });

          // Create charge item line
          if (chargeRes.data?.id) {
            await api.post('/charge-items', {
              charge_id: chargeRes.data.id,
              freight_code: dc.freight_code || '',
              freight_description: dc.freight_description || '',
              pc: chargeType === 'AP' ? 'C' : 'P',
              unit: dc.unit || 'UNIT',
              volume: dc.volume || 1,
              rate: dc.rate || 0,
              amount: dc.amount || 0,
              agent_amount: dc.agent_amount || 0,
              status: 'Open',
            });
          }
          created++;
        } catch (err) {
          console.error('Failed to create charge from default:', err);
        }
      }

      toast.success(`Loaded ${created} default charge(s) from ${tp.name}`);
      setShowLoadTP(false);
      setTpSearch('');
      setTpResults([]);
      loadData(); // Refresh
    } catch (error) {
      toast.error('Failed to load default charges');
    } finally {
      setLoadingTP(false);
    }
  };

  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading...</div>;
  }

  if (!shipmentId) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
        <p>Please save the shipment first.</p>
      </div>
    );
  }

  const renderChargeRow = (charge) => {
    const balance = (Number(charge.revenue) || 0) - (Number(charge.cost) || 0);
    return (
      <tr key={charge.id}>
        <td>
          <span className="invoice-link" onClick={() => navigate(`/charge/${charge.id}`)}>
            {charge.invoice_no || charge.vendor_invoice_no || `#${charge.id}`}
          </span>
        </td>
        <td>
          <input
            type="text"
            className="cell-input"
            value={charge.party || charge.bill_to || charge.vendor || charge.agent_name || ''}
            onChange={(e) => updateCharge(charge.id, 'party', e.target.value)}
            placeholder="Party name"
            style={{ cursor: 'pointer' }}
            onClick={(e) => { if (!e.target.value) navigate(`/charge/${charge.id}`); }}
          />
        </td>
        <td className="col-number">
          <span className={Number(charge.revenue) > 0 ? 'amount-positive' : 'amount-zero'}>
            {formatCurrency(charge.revenue)}
          </span>
        </td>
        <td className="col-number">
          <span className={Number(charge.cost) > 0 ? 'amount-positive' : 'amount-zero'}>
            {formatCurrency(charge.cost)}
          </span>
        </td>
        <td className={`col-number ${balance >= 0 ? 'amount-positive' : 'amount-negative'}`}>
          {formatCurrency(balance)}
        </td>
        <td>
          <select
            className="cell-select status-select"
            value={charge.status || 'Draft'}
            onChange={(e) => updateCharge(charge.id, 'status', e.target.value)}
          >
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </td>
        <td>
          <input
            type="date"
            className="cell-date"
            value={charge.post_date ? charge.post_date.split('T')[0] : ''}
            onChange={(e) => updateCharge(charge.id, 'post_date', e.target.value)}
          />
        </td>
        <td>
          <input
            type="date"
            className="cell-date"
            value={charge.invoice_date ? charge.invoice_date.split('T')[0] : ''}
            onChange={(e) => updateCharge(charge.id, 'invoice_date', e.target.value)}
          />
        </td>
        <td className="col-action">
          <button className="btn-action-delete" onClick={() => deleteCharge(charge.id)} title="Delete">
            <FiTrash2 />
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div className="accounting-tab">
      {/* Top Action Buttons */}
      <div className="accounting-toolbar">
        <div className="accounting-toolbar-left">
          <button className="btn-create-invoice" onClick={() => goToNewCharge('AR')}>
            <FiPlus size={12} /> Create Invoice
          </button>
          <button className="btn-create-dc" onClick={() => goToNewCharge('DC')}>
            <FiPlus size={12} /> Create D/C Note
          </button>
          <button className="btn-create-cost" onClick={() => goToNewCharge('AP')}>
            <FiPlus size={12} /> Create Cost
          </button>
          <button
            className="btn-create-invoice"
            onClick={() => invoiceInputRef.current?.click()}
            style={{ background: '#059669', marginLeft: '8px' }}
          >
            <FiUpload size={12} /> Upload Invoice
          </button>
          <input
            ref={invoiceInputRef}
            type="file"
            accept=".pdf"
            onChange={handleInvoiceUpload}
            style={{ display: 'none' }}
          />
          <button
            className="btn-create-invoice"
            onClick={() => setShowLoadTP(!showLoadTP)}
            style={{ background: '#7c3aed', marginLeft: '8px' }}
          >
            <FiDownload size={12} /> Default Freight
          </button>
        </div>
        <div className="accounting-toolbar-right">
          <label className="draft-checkbox">
            <input
              type="checkbox"
              checked={includeDraft}
              onChange={(e) => setIncludeDraft(e.target.checked)}
            />
            Include Draft Amount
          </label>
        </div>
      </div>

      {/* Trade Partner Search for Default Freight */}
      {showLoadTP && (
        <div style={{ padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Search Trade Partner:</label>
            <input
              type="text"
              value={tpSearch}
              onChange={(e) => searchTP(e.target.value)}
              placeholder="Type name or code..."
              style={{ flex: 1, maxWidth: '300px', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
              autoFocus
            />
            <button className="btn btn-sm btn-secondary" onClick={() => { setShowLoadTP(false); setTpSearch(''); setTpResults([]); }}>
              Cancel
            </button>
          </div>
          {tpResults.length > 0 && (
            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff' }}>
              {tpResults.map(tp => (
                <div
                  key={tp.id}
                  onClick={() => !loadingTP && loadDefaultFreight(tp)}
                  style={{
                    padding: '8px 12px', cursor: loadingTP ? 'wait' : 'pointer', fontSize: '13px',
                    borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '12px', alignItems: 'center',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                >
                  <span style={{ fontWeight: 600, color: '#2563eb', minWidth: '70px' }}>{tp.code}</span>
                  <span style={{ fontWeight: 500 }}>{tp.name}</span>
                  <span style={{ color: '#94a3b8', fontSize: '11px' }}>{tp.tp_type}</span>
                  {tp.city && <span style={{ color: '#94a3b8', fontSize: '11px', marginLeft: 'auto' }}>{tp.city}</span>}
                </div>
              ))}
            </div>
          )}
          {tpSearch.length > 0 && tpResults.length === 0 && (
            <div style={{ padding: '8px 12px', color: '#94a3b8', fontSize: '12px' }}>No trade partners found</div>
          )}
        </div>
      )}

      {/* Main Accounting Table */}
      <div className="accounting-table-wrapper">
        <table className="accounting-table">
          <thead>
            <tr>
              <th>Invoice No.</th>
              <th>Party</th>
              <th className="col-number">Revenue</th>
              <th className="col-number">Cost</th>
              <th className="col-number">Balance</th>
              <th>Status</th>
              <th>Post Date</th>
              <th>Invoice Date</th>
              <th className="col-action">Action</th>
            </tr>
          </thead>
          <tbody>
            {/* Invoice (A/R) Section */}
            <tr className="section-label-row">
              <td className="section-label" rowSpan={Math.max(filteredAR.length, 1) + 1}>
                <div className="section-label-text">Invoice (A/R)</div>
              </td>
            </tr>
            {filteredAR.length === 0 ? (
              <tr className="empty-section-row">
                <td colSpan="8" className="empty-section-cell">No invoices</td>
              </tr>
            ) : (
              filteredAR.map(renderChargeRow)
            )}

            {/* D/C Note Section */}
            <tr className="section-label-row">
              <td className="section-label" rowSpan={Math.max(filteredDC.length, 1) + 1}>
                <div className="section-label-text">D/C Note</div>
              </td>
            </tr>
            {filteredDC.length === 0 ? (
              <tr className="empty-section-row">
                <td colSpan="8" className="empty-section-cell">No D/C notes</td>
              </tr>
            ) : (
              filteredDC.map(renderChargeRow)
            )}

            {/* Cost (A/P) Section */}
            <tr className="section-label-row">
              <td className="section-label" rowSpan={Math.max(filteredAP.length, 1) + 1}>
                <div className="section-label-text">Cost (A/P)</div>
              </td>
            </tr>
            {filteredAP.length === 0 ? (
              <tr className="empty-section-row">
                <td colSpan="8" className="empty-section-cell">No costs</td>
              </tr>
            ) : (
              filteredAP.map(renderChargeRow)
            )}
          </tbody>

          {/* Totals */}
          <tfoot>
            <tr className="totals-row">
              <td colSpan="2" className="totals-label">Total</td>
              <td className={`col-number ${totalRevenue > 0 ? 'amount-positive' : ''}`}>
                {formatCurrency(totalRevenue)}
              </td>
              <td className={`col-number ${totalCost > 0 ? 'amount-positive' : ''}`}>
                {formatCurrency(totalCost)}
              </td>
              <td className={`col-number ${totalBalance >= 0 ? 'amount-positive' : 'amount-negative'}`}>
                {formatCurrency(totalBalance)}
              </td>
              <td colSpan="4"></td>
            </tr>
            <tr className="amount-row">
              <td colSpan="2" className="totals-label">Amount</td>
              <td colSpan="2" className={`col-number ${totalBalance >= 0 ? 'amount-positive' : 'amount-negative'}`}>
                {formatCurrency(totalBalance)}
              </td>
              <td colSpan="5"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Profit Section */}
      <div className="profit-section">
        <table className="profit-table">
          <thead>
            <tr>
              <th></th>
              <th className="col-number">Amount</th>
              <th className="col-number">Profit Percentage</th>
              <th className="col-number">Profit Margin</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="profit-label">Total Profit</td>
              <td className={`col-number ${totalProfit >= 0 ? 'amount-positive' : 'amount-negative'}`}>
                {formatCurrency(totalProfit)}
              </td>
              <td className={`col-number ${profitPercentage >= 0 ? 'amount-positive' : 'amount-negative'}`}>
                {profitPercentage.toFixed(2)}%
              </td>
              <td className={`col-number ${profitMargin >= 0 ? 'amount-positive' : 'amount-negative'}`}>
                {profitMargin.toFixed(2)}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Memo */}
      <div className="accounting-memo-section">
        <div className="form-section-header">Memo</div>
        <textarea
          className="shipment-memo"
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="Add memo..."
        />
      </div>

      {/* Invoice Upload Modal */}
      {invoiceModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', width: '600px', maxHeight: '80vh',
            overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '16px 20px', borderBottom: '1px solid #e2e8f0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                {invoiceParsing ? 'Parsing Invoice...' : 'Review Invoice — Create A/P'}
              </h3>
              <button onClick={() => { setInvoiceModal(false); setInvoiceData(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '18px' }}>
                <FiX />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px' }}>
              {invoiceParsing ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  <div className="spinner" style={{ display: 'inline-block', marginBottom: '12px' }} />
                  <div>Extracting charges from invoice...</div>
                </div>
              ) : invoiceData ? (
                <>
                  {/* Vendor & Invoice Info */}
                  <div style={{ display: 'flex', gap: '8px', padding: '10px 14px', background: '#f0fdf4', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#166534', alignItems: 'center' }}>
                    <FiFileText />
                    <span>Invoice parsed successfully — <strong>{invoiceData.charges.length}</strong> charge(s) found</span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px', fontSize: '13px' }}>
                    <div>
                      <label style={{ fontWeight: 600, fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '3px' }}>Vendor</label>
                      <input type="text" value={invoiceData.vendor || ''} onChange={(e) => handleInvoiceFieldChange('vendor', e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                    </div>
                    <div>
                      <label style={{ fontWeight: 600, fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '3px' }}>Invoice No.</label>
                      <input type="text" value={invoiceData.invoice_no || ''} onChange={(e) => handleInvoiceFieldChange('invoice_no', e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                    </div>
                    <div>
                      <label style={{ fontWeight: 600, fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '3px' }}>Invoice Date</label>
                      <input type="date" value={invoiceData.invoice_date || ''} onChange={(e) => handleInvoiceFieldChange('invoice_date', e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                    </div>
                    <div>
                      <label style={{ fontWeight: 600, fontSize: '11px', color: '#64748b', display: 'block', marginBottom: '3px' }}>Due Date</label>
                      <input type="date" value={invoiceData.due_date || ''} onChange={(e) => handleInvoiceFieldChange('due_date', e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
                    </div>
                  </div>

                  {/* Reference Numbers */}
                  {invoiceData.reference_nos && invoiceData.reference_nos.length > 0 && (
                    <div style={{ marginBottom: '14px', fontSize: '12px', color: '#475569' }}>
                      <span style={{ fontWeight: 600 }}>References: </span>
                      {invoiceData.reference_nos.map((r, i) => (
                        <span key={i} style={{
                          display: 'inline-block', padding: '2px 8px', background: '#f1f5f9',
                          borderRadius: '4px', marginRight: '6px', marginBottom: '4px', fontSize: '11px',
                        }}>
                          <span style={{ color: '#94a3b8' }}>{r.type}: </span>
                          <strong>{r.value}</strong>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Charge Lines Table */}
                  <div style={{ fontWeight: 600, fontSize: '13px', color: '#475569', marginBottom: '8px' }}>
                    Charges:
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '12px' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                        <th style={{ padding: '6px 10px', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Description</th>
                        <th style={{ padding: '6px 10px', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0', width: '130px', textAlign: 'right' }}>Amount</th>
                        <th style={{ padding: '6px 10px', width: '40px', borderBottom: '2px solid #e2e8f0' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceData.charges.map((ch, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '5px 10px' }}>
                            <input type="text" value={ch.description} onChange={(e) => handleChargeDescChange(i, e.target.value)}
                              style={{ width: '100%', padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px' }} />
                          </td>
                          <td style={{ padding: '5px 10px', textAlign: 'right' }}>
                            <input type="number" step="0.01" value={ch.amount} onChange={(e) => handleChargeAmountChange(i, e.target.value)}
                              style={{ width: '110px', padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px', textAlign: 'right' }} />
                          </td>
                          <td style={{ padding: '5px', textAlign: 'center' }}>
                            <button onClick={() => removeChargeLine(i)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '14px', padding: '2px' }}>
                              <FiX />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td style={{ padding: '5px 10px' }}>
                          <button onClick={addChargeLine}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: '12px', fontWeight: 600, padding: '4px 0' }}>
                            + Add Line
                          </button>
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, fontSize: '14px', borderTop: '2px solid #1e293b' }}>
                          ${invoiceData.total_amount?.toFixed(2) || '0.00'}
                        </td>
                        <td style={{ borderTop: '2px solid #1e293b' }}></td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              ) : null}
            </div>

            {/* Modal Footer */}
            {invoiceData && !invoiceParsing && (
              <div style={{
                padding: '14px 20px', borderTop: '1px solid #e2e8f0',
                display: 'flex', gap: '8px', justifyContent: 'flex-end',
              }}>
                <button className="btn" onClick={() => { setInvoiceModal(false); setInvoiceData(null); }}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={createAPFromInvoice} disabled={invoiceCreating}
                  style={{ background: '#059669', borderColor: '#059669' }}>
                  {invoiceCreating ? 'Creating...' : <><FiCheck /> Create A/P</>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountingTab;
